import React, { useState } from 'react';
import { pdfViewer } from '../hooks/usePdfViewer';
import memoryApi from '../api/memoryApi';
import { Loader2 } from 'lucide-react';

/**
 * SourcesBanner - Horizontal scrollable document references
 * Document/book style design, distinct from media gallery
 *
 * Props:
 *   sources: Array of {file_id, title, page, category}
 */

const SourcesBanner = ({ sources }) => {
  const [loadingId, setLoadingId] = useState(null);

  if (!sources?.length) return null;

  const handleClick = async (source) => {
    try {
      setLoadingId(source.file_id);
      const result = await memoryApi.getRagPdfUrl(source.file_id);
      if (result.pdf_url) {
        pdfViewer.open(result.pdf_url, source.title, source.page || 1, source.file_id);
      }
    } catch (error) {
      console.error('Failed to get PDF URL:', error);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border-color, rgba(128,128,128,0.2))' }}>
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-tertiary, #888)' }}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--text-tertiary, #888)' }}>
          References
        </span>
      </div>

      {/* Horizontal scroll container */}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
        {sources.map((source) => (
          <button
            key={source.file_id}
            onClick={() => handleClick(source)}
            disabled={loadingId === source.file_id}
            className="flex-shrink-0 group"
          >
            {/* Document card - book/paper style */}
            <div
              className="relative w-20 h-28 rounded shadow-md transition-all group-hover:shadow-lg group-hover:-translate-y-0.5 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '1px solid rgba(0,0,0,0.1)',
              }}
            >
              {/* Loading overlay */}
              {loadingId === source.file_id && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <Loader2 size={20} className="animate-spin text-blue-500" />
                </div>
              )}

              {/* PDF icon area */}
              <div className="h-16 flex items-center justify-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}>
                <svg width="28" height="32" viewBox="0 0 28 32" fill="none" className="opacity-80">
                  <path d="M2 0C0.9 0 0 0.9 0 2V30C0 31.1 0.9 32 2 32H26C27.1 32 28 31.1 28 30V8L20 0H2Z" fill="#DC2626"/>
                  <path d="M22 8H28L20 0V6C20 7.1 20.9 8 22 8Z" fill="#EF4444"/>
                  <text x="14" y="22" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">PDF</text>
                </svg>
              </div>

              {/* Title area */}
              <div className="p-1.5">
                <p
                  className="text-xs font-medium leading-tight line-clamp-2"
                  style={{ color: '#374151' }}
                  title={source.title}
                >
                  {source.title}
                </p>
              </div>

              {/* Category badge - positioned at bottom */}
              {source.category && (
                <div
                  className="absolute bottom-1 left-1 right-1 text-center text-[10px] px-1 py-0.5 rounded truncate"
                  style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#2563eb' }}
                >
                  {source.category}
                </div>
              )}
            </div>

          </button>
        ))}
      </div>
    </div>
  );
};

export default SourcesBanner;
